import { useState, useEffect } from 'react';
import { getColorPresets, type ColorPreset } from '@/lib/api';
import { LayerColors } from '@/types/canvas';
import { cn } from '@/lib/utils';

interface ColorPresetsButtonsProps {
  selectedPresetId?: string;
  onPresetSelect: (colors: LayerColors, presetId?: string) => void;
}

export function ColorPresetsButtons({ selectedPresetId, onPresetSelect }: ColorPresetsButtonsProps) {
  const [presets, setPresets] = useState<ColorPreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    setIsLoading(true);
    try {
      const result = await getColorPresets(false); // Pouze veřejné presety
      if (result.success && result.data) {
        const presetsArray = Array.isArray(result.data) ? result.data : [];
        // Filtrovat pouze presety s tagem 'colorScheme' (výchozí presety)
        const colorSchemePresets = presetsArray.filter((p: ColorPreset) =>
          p.tags?.includes('colorScheme') || p.is_public
        );
        // Seřadit podle sort_order
        const sortedPresets = colorSchemePresets.sort((a: ColorPreset, b: ColorPreset) => (a.sort_order || 0) - (b.sort_order || 0));
        setPresets(sortedPresets);
      }
    } catch (error) {
      console.error('[ColorPresetsButtons] Chyba při načítání presetů:', error);
      setPresets([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePresetSelect = (preset: ColorPreset) => {
    onPresetSelect(preset.colors, preset.id);
  };

  if (isLoading) {
    return (
      <div className="mt-4">
        <div className="flex gap-2 pb-2">
          <div className="px-4 py-2 text-sm text-muted-foreground">Načítání...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="flex gap-2 overflow-x-auto lg:overflow-x-visible lg:flex-wrap pb-2 scrollbar-hide">
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => handlePresetSelect(preset)}
            className={cn(
              'flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
              selectedPresetId === preset.id
                ? 'bg-primary text-primary-foreground'
                : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            )}
          >
            {preset.name}
          </button>
        ))}
      </div>
    </div>
  );
}
